import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Grid,
  message,
  Row,
  Space,
  Typography,
  FloatButton,
  Badge,
  Drawer,
} from "antd";

import {
  ArrowLeftOutlined,
  CheckOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
  SaveOutlined,
} from "@ant-design/icons";

import { useNavigate } from "react-router-dom";

import api from "../../../api/axios";

import usePurchaseOrderCart from "../../../hooks/usePurchaseOrderCart";

import SupplierInformation from "../../../components/SupplierInformation";
import ProductBrowser from "../../../components/ProductBrowser";
import CartPanel from "../../../components/CartPanel";
import OrderSummary from "../../../components/OrderSummary";
import ShippingInformation from "../../../components/ShippingInformation";
import ReviewOrderModal from "../../../components/ReviewOrderModal";

const { Title, Text } = Typography;

const CreatePurchaseOrder = () => {
  const navigate = useNavigate();

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [form] = Form.useForm();

  const [spareparts, setSpareparts] =
    useState([]);
const [searchText, setSearchText] = useState("");

const [sparepartPagination, setSparepartPagination] =
  useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [suppliers, setSuppliers] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [reviewOpen, setReviewOpen] =
    useState(false);

  const [cartDrawerOpen, setCartDrawerOpen] =
    useState(false);

  const [messageApi, contextHolder] =
    message.useMessage();

  const {
    cart,
    totals,

    addToCart,
    addManyToCart,

    removeFromCart,
    removeItems,

    updateQuantity,
    increaseQuantity,
    decreaseQuantity,
    updateUnitCost,

    clearCart,
  } = usePurchaseOrderCart();

  /* ============================================================
     LOAD DATA
  ============================================================ */

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([
      loadSuppliers(),
      fetchSpareparts(),
    ]);
  };

  const loadSuppliers = async () => {
    /*
     * Replace this with your supplier API when available.
     */
    setSuppliers([
      {
        id: 1,
        name: "Global Auto Parts Ltd.",
        contact: "Mr. Lee",
        country: "China",
      },
      {
        id: 2,
        name: "Euro Spares Inc.",
        contact: "Ms. Schmidt",
        country: "Germany",
      },
      {
        id: 3,
        name: "American Machinery Co.",
        contact: "Mr. Smith",
        country: "USA",
      },
    ]);
  };

 const fetchSpareparts = async (
  page = 1,
  search = searchText
) => {
  try {
    const response = await api.get(
      "/api/purchase-orders/spareparts",
      {
        params: {
          page,
          limit: 50,
          search,
        },
      }
    );

    const result = response.data;

    setSpareparts(result.data || []);

    setSparepartPagination(
      result.pagination || {
        page,
        limit: 50,
        total: 0,
        totalPages: 0,
      }
    );
  } catch (error) {
    console.error(
      "Failed to load spare parts:",
      error
    );

    messageApi.error(
      "Failed to load spare parts"
    );
  }
};
const handleSparepartSearch = (
  value
) => {
  const search = value || "";

  setSearchText(search);

  fetchSpareparts(1, search);
};
const handleSparepartPageChange = (
  page
) => {
  fetchSpareparts(
    page,
    searchText
  );
};
  /* ============================================================
     FORM VALUES
  ============================================================ */

  const formValues = Form.useWatch(
    [],
    form
  );

  const currency =
    formValues?.currency || "USD";

  const shippingCost = Number(
    formValues?.shippingCost || 0
  );

  /* ============================================================
     SUPPLIER NAME
  ============================================================ */

  const supplierName = useMemo(() => {
    const supplier = suppliers.find(
      (item) =>
        String(item.id) ===
        String(formValues?.supplierId)
    );

    return supplier?.name || "";
  }, [
    suppliers,
    formValues?.supplierId,
  ]);

  /* ============================================================
     OPEN REVIEW
  ============================================================ */

  const handleReview = async () => {
    try {
      await form.validateFields();

      if (cart.length === 0) {
        messageApi.warning(
          "Please add at least one spare part."
        );

        return;
      }

      setReviewOpen(true);
    } catch (error) {
      messageApi.warning(
        "Please complete the required order information."
      );
    }
  };

  /* ============================================================
     CREATE PO
  ============================================================ */

  const handleCreatePurchaseOrder =
    async () => {
      try {
        setLoading(true);

        const values =
          await form.validateFields();

        if (cart.length === 0) {
          messageApi.warning(
            "Please add at least one spare part."
          );

          return;
        }

        const subtotal = cart.reduce(
          (sum, item) =>
            sum +
            Number(item.quantity || 0) *
              Number(item.unitCost || 0),
          0
        );

        const shipping =
          Number(values.shippingCost || 0);

        const payload = {
          ...values,

          purchaseOrderId: `PO-${Date.now()}`,

          supplierId: values.supplierId,

          orderDate: values.orderDate
            ? values.orderDate.toISOString()
            : null,

          totalCost:
            subtotal + shipping,

          items: cart.map((item) => ({
            sparepartId:
              item.sparepartId,

            quantity: Number(
              item.quantity || 0
            ),

            unitCost: Number(
              item.unitCost || 0
            ),

            total:
              Number(item.quantity || 0) *
              Number(item.unitCost || 0),
          })),
        };

        console.log(
          "Purchase Order Payload:",
          payload
        );

        await api.post(
          "/api/purchase-orders",
          payload
        );

        messageApi.success(
          "Purchase Order created successfully."
        );

        setReviewOpen(false);

        navigate(
          "/admin/purchase-order"
        );
      } catch (error) {
        console.error(
          "Failed to create purchase order:",
          error
        );

        messageApi.error(
          error?.response?.data?.message ||
            "Failed to create Purchase Order"
        );
      } finally {
        setLoading(false);
      }
    };

  /* ============================================================
     RESET
  ============================================================ */

  const handleReset = () => {
    form.resetFields();
    clearCart();
  };

  /* ============================================================
     MOBILE CART
  ============================================================ */

  const mobileCart = (
    <CartPanel
      cart={cart}
      totals={totals}
      onIncrease={increaseQuantity}
      onDecrease={decreaseQuantity}
      onQuantityChange={updateQuantity}
      onCostChange={updateUnitCost}
      onRemove={removeFromCart}
      onRemoveItems={removeItems}
      onClear={clearCart}
      isMobile
    />
  );

  return (
    <div
      style={{
        maxWidth: 1600,
        margin: "0 auto",
        paddingBottom: isMobile
          ? 100
          : 40,
      }}
    >
      {contextHolder}

      {/* ========================================================
          HEADER
      ======================================================== */}

      <div
        style={{
          marginBottom: 20,
        }}
      >
        

        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: isMobile
              ? "flex-start"
              : "center",
            flexDirection: isMobile
              ? "column"
              : "row",
            gap: 12,
          }}
        >
          <div>
            <Title
              level={isMobile ? 3 : 2}
              style={{
                margin: 0,
              }}
            >
              Create Purchase Order
            </Title>

            <Text type="secondary">
              Build your purchase order,
              add multiple spare parts,
              edit quantities and review
              before submission.
            </Text>
          </div>

          <Badge
            count={totals.itemCount}
            overflowCount={9999}
            showZero
          >
            <Button
              size="large"
              icon={<ShoppingCartOutlined />}
              onClick={() => {
                if (isMobile) {
                  setCartDrawerOpen(true);
                }
              }}
            >
              {isMobile
                ? "Cart"
                : `${totals.itemCount} Items`}
            </Button>
          </Badge>
        </div>
      </div>

      {/* ========================================================
          FORM
      ======================================================== */}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          currency: "USD",
          shippingCost: 0,
        }}
      >
        <SupplierInformation
          suppliers={suppliers}
          isMobile={isMobile}
        />

        {/* ======================================================
            DESKTOP WORKSPACE
        ====================================================== */}

        {!isMobile && (
          <>
            <Row
              gutter={[20, 20]}
              align="top"
            >
              <Col
                xs={24}
                xl={17}
              >
              <ProductBrowser
  spareparts={spareparts}
  cart={cart}
  onAddToCart={addToCart}
  onAddManyToCart={addManyToCart}
  onQuantityChange={updateQuantity}
  searchText={searchText}
  pagination={sparepartPagination}
  onSearch={handleSparepartSearch}
  onPageChange={handleSparepartPageChange}
  isMobile={false}
/>

                <div
                  style={{
                    marginTop: 20,
                  }}
                >
                  <CartPanel
                    cart={cart}
                    totals={totals}
                    onIncrease={
                      increaseQuantity
                    }
                    onDecrease={
                      decreaseQuantity
                    }
                    onQuantityChange={
                      updateQuantity
                    }
                    onCostChange={
                      updateUnitCost
                    }
                    onRemove={
                      removeFromCart
                    }
                    onRemoveItems={
                      removeItems
                    }
                    onClear={clearCart}
                    isMobile={false}
                  />
                </div>
              </Col>

              <Col
                xs={24}
                xl={7}
              >
                <OrderSummary
                  totals={totals}
                  shippingCost={
                    shippingCost
                  }
                  currency={currency}
                  sticky
                />
              </Col>
            </Row>
          </>
        )}

        {/* ======================================================
            MOBILE WORKSPACE
        ====================================================== */}

        {isMobile && (
          <>
         <ProductBrowser
  spareparts={spareparts}
  cart={cart}
  onAddToCart={addToCart}
  onAddManyToCart={addManyToCart}
  onQuantityChange={updateQuantity}
  searchText={searchText}
  pagination={sparepartPagination}
  onSearch={handleSparepartSearch}
  onPageChange={handleSparepartPageChange}
  isMobile
/>

            <ShippingInformation />

            <Card
              style={{
                marginTop: 20,
                borderRadius: 14,
              }}
            >
              <OrderSummary
                totals={totals}
                shippingCost={
                  shippingCost
                }
                currency={currency}
              />
            </Card>
          </>
        )}

        {/* ======================================================
            SHIPPING DESKTOP
        ====================================================== */}

        {!isMobile && (
          <ShippingInformation />
        )}

        {/* ======================================================
            ACTIONS
        ====================================================== */}

        <Divider />

        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            gap: 12,
            flexDirection: isMobile
              ? "column"
              : "row",
          }}
        >
          <Button
            size="large"
            block={isMobile}
            icon={<ArrowLeftOutlined />}
            onClick={() =>
              navigate(
                "/admin/purchase-order"
              )
            }
          >
            Back to Purchase Orders
          </Button>

          <Space
            style={{
              width: isMobile
                ? "100%"
                : "auto",
              justifyContent: isMobile
                ? "stretch"
                : "flex-end",
            }}
            direction={
              isMobile
                ? "vertical"
                : "horizontal"
            }
          >
            <Button
              size="large"
              block={isMobile}
              icon={<SaveOutlined />}
              onClick={handleReset}
            >
              Reset
            </Button>

            <Button
              type="primary"
              size="large"
              block={isMobile}
              icon={<CheckOutlined />}
              disabled={
                cart.length === 0
              }
              onClick={handleReview}
            >
              Review & Create PO
            </Button>
          </Space>
        </div>
      </Form>

      {/* ========================================================
          MOBILE FLOATING CART
      ======================================================== */}

      {isMobile && cart.length > 0 && (
        <FloatButton
          icon={<ShoppingCartOutlined />}
          badge={{
            count: totals.itemCount,
            overflowCount: 9999,
          }}
          onClick={() =>
            setCartDrawerOpen(true)
          }
          type="primary"
        />
      )}

      {/* ========================================================
          MOBILE CART DRAWER
      ======================================================== */}

      <Drawer
        title={
          <Space>
            <ShoppingCartOutlined />
            Purchase Cart
          </Space>
        }
        placement="bottom"
        height="92%"
        open={cartDrawerOpen}
        onClose={() =>
          setCartDrawerOpen(false)
        }
        destroyOnClose={false}
        styles={{
          body: {
            padding: 12,
          },
        }}
      >
        {mobileCart}

        <div
          style={{
            position: "sticky",
            bottom: 0,
            marginTop: 12,
            paddingTop: 10,
            background: "#fff",
          }}
        >
          <Button
            type="primary"
            size="large"
            block
            disabled={
              cart.length === 0
            }
            onClick={() => {
              setCartDrawerOpen(false);
              handleReview();
            }}
          >
            Review Order
          </Button>
        </div>
      </Drawer>

      {/* ========================================================
          REVIEW MODAL
      ======================================================== */}

      <ReviewOrderModal
        open={reviewOpen}
        onCancel={() =>
          setReviewOpen(false)
        }
        onConfirm={
          handleCreatePurchaseOrder
        }
        cart={cart}
        totals={totals}
        formValues={{
          ...formValues,
          supplierName,
        }}
        loading={loading}
      />
    </div>
  );
};

export default CreatePurchaseOrder;